import { useMemo, useRef } from 'react';
import {
  Modal,
  ModalSizes,
  Alert,
  AlertVariants,
  AlertAppearances,
  AlertOrientations,
  ButtonVariants,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { LoadingOverlay } from '@/components/feedback';
import { DiscrepancyDetailCard } from './DiscrepancyDetailCard';
import styles from './DiscrepancyModal.module.scss';

/**
 * Props:
 *   open          whether the modal is shown
 *   onClose       close handler
 *   referenceId   shown in the header: "Discrepancies: {referenceId}"
 *   message       a single mismatch sentence (node), shown as one warning Alert —
 *                 use this OR `discrepancies`, not both
 *   discrepancies Array<{ id, type, message }> — one alert each; tone comes from `type`
 *   loading       shows a loading row instead of the body while the content is being fetched
 *                 Alert per entry, for a mix of severities in a single review
 *   sections      Array<{ title, fields: Array<{ label, value, type?: 'status', tone? }> }>
 *                 omit entirely for a message-only discrepancy (no detail card)
 *   actions       Array<{ label, variant?, onClick, disabled? }> — Modal's own button
 *                 shape (it renders the footer itself); defaults to a single "Go back"
 */
// Alert tone per backend discrepancy type (discrepancyHelper.js); default is a warning.
const DISCREPANCY_VARIANTS = Object.freeze({
  SUSTAINABILITY_MISMATCH: AlertVariants.warning,
  QUANTITY_EXCEEDED: AlertVariants.danger,
  ACTUAL_EXCEEDS_CONTRACTED: AlertVariants.danger,
  SHIPMENT_OVER_ALLOCATED: AlertVariants.danger,
  DELIVER_BY_APPROACHING: AlertVariants.info,
  DECLARATION_DEADLINE_APPROACHING: AlertVariants.info,
});

export const DiscrepancyModal = ({
  open,
  onClose,
  referenceId,
  message,
  discrepancies,
  sections,
  actions,
  loading = false,
}) => {
  const t = useTranslations();

  // Retained across the close so the caller clearing its row can't empty the dialog mid-exit.
  const shown = useRef({ referenceId, message, discrepancies, sections });
  if (open) shown.current = { referenceId, message, discrepancies, sections };
  const content = useMemo(
    () => (open ? { referenceId, message, discrepancies, sections } : shown.current),
    [open, referenceId, message, discrepancies, sections],
  );

  const title = t('DISCREPANCY_MODAL_TITLE', { referenceId: content.referenceId });

  const alerts = useMemo(
    () =>
      content.discrepancies?.map((item) => ({
        id: item.id,
        message: item.message,
        variant: DISCREPANCY_VARIANTS[item.type] ?? AlertVariants.warning,
      })) ?? [{ id: 'message', variant: AlertVariants.warning, message: content.message }],
    [content],
  );

  const buttons = useMemo(
    () =>
      actions ?? [
        { label: t('DISCREPANCY_GO_BACK'), variant: ButtonVariants.primary, onClick: onClose },
      ],
    [actions, onClose, t],
  );

  return (
    <Modal open={open} onClose={onClose} size={ModalSizes.medium} title={title} buttons={buttons}>
      <div className={styles.body}>
        {loading && <LoadingOverlay label={t('COMMON_LOADING')} />}
        {!loading &&
          alerts.map((alert) => (
            <Alert
              key={alert.id}
              variant={alert.variant}
              appearance={AlertAppearances.accent}
              orientation={AlertOrientations.horizontal}
            >
              {alert.message}
            </Alert>
          ))}
        {!loading && content.sections?.length > 0 && (
          <DiscrepancyDetailCard sections={content.sections} />
        )}
      </div>
    </Modal>
  );
};
